import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Mail, User as UserIcon, ShieldCheck, Settings, Globe, ArrowRight, Bell, Coins, Wallet } from "lucide-react";
import BillingSection from "@/components/BillingSection";

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  // Perfil ya no toca dinero. Las cuentas se administran en «Mi dinero», que
  // es donde una persona las busca — estaban aquí, en el cajón de ajustes.
  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Mi cuenta</div>
          <h1 className="text-2xl sm:text-3xl font-bold mt-1">Perfil</h1>
        </div>
        {user?.is_superadmin && (
          <Link
            to="/admin"
            data-testid="profile-admin-link"
            className="flex items-center gap-2 text-xs font-semibold bg-gradient-to-r from-amber-500/20 to-amber-500/10 border border-amber-400/30 text-amber-200 hover:text-amber-100 hover:border-amber-300/50 px-4 py-2 rounded-full transition-all"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            Panel Admin
            <ArrowRight className="w-3 h-3" />
          </Link>
        )}
      </div>

      <div className="card-surface p-6 sm:p-7 flex items-center gap-5 flex-wrap" data-testid="profile-card">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center text-3xl font-bold text-white shadow-[0_10px_40px_-12px_rgba(139,92,246,0.6)]">
          {user?.name?.[0]?.toUpperCase() || "U"}
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-xl font-bold">{user?.name}</div>
          <div className="text-sm text-slate-400 flex items-center gap-1.5 mt-1"><Mail className="w-3.5 h-3.5"/>{user?.email}</div>
          <div className="flex items-center gap-2 mt-3">
            <span className="inline-flex items-center gap-1.5 text-xs bg-emerald-500/15 text-emerald-300 px-2.5 py-1 rounded-full border border-emerald-500/20">
              <ShieldCheck className="w-3 h-3"/> Cuenta verificada
            </span>
          </div>
        </div>
        <button onClick={doLogout} data-testid="profile-logout-btn" className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-500/10 text-rose-300 border border-rose-500/20 hover:bg-rose-500/20 text-sm font-medium">
          <LogOut className="w-4 h-4"/> Cerrar sesión
        </button>
      </div>

      {/* Billing & subscription */}
      <BillingSection user={user}/>

      {/* Settings shortcuts */}
      {/* Las cuentas vivieron aquí mucho tiempo. Quien las busque por costumbre
          encuentra el camino en vez de un hueco. */}
      <Link to="/mi-dinero" data-testid="profile-to-my-money"
        className="flex items-center gap-3 p-4 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-white/20 min-h-16">
        <span className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center shrink-0">
          <Wallet className="w-[18px] h-[18px] text-violet-300" aria-hidden="true" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[15px] font-semibold text-white">Tus cuentas y tarjetas</span>
          <span className="block text-[13px] text-slate-400">Ahora están en «Mi dinero»</span>
        </span>
        <ArrowRight className="w-4 h-4 text-slate-500 shrink-0" aria-hidden="true" />
      </Link>

      <section data-testid="profile-settings">
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><Settings className="w-4 h-4 text-violet-400"/>Ajustes</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <Link to="/comisiones" data-testid="profile-fees"
            className="flex items-center justify-between p-4 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-white/15 transition-all group">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center">
                <Send className="w-4 h-4 text-emerald-300"/>
              </div>
              <div>
                <div className="text-sm font-bold">Comisiones interbancarias</div>
                <div className="text-xs text-slate-400">Configura tarifas por país, banco y tipo</div>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-violet-300 group-hover:translate-x-0.5 transition-all"/>
          </Link>
          <Link to="/perfil#fx" data-testid="profile-fx"
            className="flex items-center justify-between p-4 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-white/15 transition-all group">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center">
                <Globe className="w-4 h-4 text-amber-300"/>
              </div>
              <div>
                <div className="text-sm font-bold">Tasas de cambio</div>
                <div className="text-xs text-slate-400">USD, EUR, USDT — actualízalas con IA</div>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-amber-300 group-hover:translate-x-0.5 transition-all"/>
          </Link>
          <Link to="/perfil/notificaciones" data-testid="profile-notifications"
            className="flex items-center justify-between p-4 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-white/15 transition-all group">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center">
                <Bell className="w-4 h-4 text-violet-300"/>
              </div>
              <div>
                <div className="text-sm font-bold">Notificaciones</div>
                <div className="text-xs text-slate-400">Email, WhatsApp y tipos de alerta</div>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-violet-300 group-hover:translate-x-0.5 transition-all"/>
          </Link>
        </div>
      </section>
    </div>
  );
}

const CURRENCIES = ["USD", "DOP", "EUR", "USDT", "MXN", "COP", "ARS", "PEN", "CLP", "BRL", "GBP"];
const COUNTRIES = [
  { code: "DO", name: "Rep. Dominicana" },
  { code: "US", name: "Estados Unidos" },
  { code: "MX", name: "México" },
  { code: "CO", name: "Colombia" },
  { code: "AR", name: "Argentina" },
  { code: "CL", name: "Chile" },
  { code: "PE", name: "Perú" },
  { code: "BR", name: "Brasil" },
  { code: "ES", name: "España" },
  { code: "VE", name: "Venezuela" },
  { code: "EC", name: "Ecuador" },
  { code: "PA", name: "Panamá" },
  { code: "CR", name: "Costa Rica" },
  { code: "GB", name: "Reino Unido" },
  { code: "DE", name: "Alemania" },
  { code: "FR", name: "Francia" },
  { code: "CA", name: "Canadá" },
];
